import { defaultTo, set, path, view, assoc, pathSatisfies, is, reduce, keys, lensPath, propEq, over, assocPath, toLower, has, prop, equals, merge, groupBy, forEach } from 'ramda';
import assert from 'assert';
import { bind } from 'redux-effects';
import { fetch as fetch$1 } from 'redux-effects-fetch';
import { startTrackingRequest, stopTrackingRequest } from '@rushplay/api-client';
import PropTypes from 'prop-types';
import React from 'react';
import { connect, useSelector } from 'react-redux';

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  }

  return _assertThisInitialized(self);
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  }
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

// TODO: Extract to separate module

function createCacheRecord(value) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var timestamp = Date.now();
  var ttl = defaultTo(0, options.ttl);
  return {
    _expiresAt: timestamp + ttl,
    value: value
  };
}

function createCache() {
  var data = new Map();

  function check(key) {
    if (!data.has(key)) {
      return false;
    }

    var timestamp = Date.now();
    var record = data.get(key);
    return record._expiresAt > timestamp;
  }

  function get(key) {
    if (check(key)) {
      var record = data.get(key);
      return record.value;
    }
  }

  function set(key, value, options) {
    data.set(key, createCacheRecord(value, options));
  }

  return {
    check: check,
    get: get,
    set: set
  };
}

var E_TOKEN_LENGTH = new SyntaxError('Left and right tokens must have equal width');
var ALIAS_TOKEN = '@:';
var LEFT_TOKEN = '{{';
var RIGHT_TOKEN = '}}';
var PIPE_TOKEN = '|';
var E_TOKEN_MISSING = new SyntaxError("One of closing tokens '".concat(RIGHT_TOKEN, "' not found"));
var TOKEN_LENGTH = LEFT_TOKEN.length === RIGHT_TOKEN.length && LEFT_TOKEN.length; // sanity check

assert(TOKEN_LENGTH, E_TOKEN_LENGTH); // --

var INTERPOLATION_START_TOKEN = '{{';
var INTERPOLATION_END_TOKEN = '}}';

function hasAlias(input) {
  return input.indexOf(ALIAS_TOKEN) === 0;
}

function hasInterpolation(input) {
  return input.indexOf(INTERPOLATION_START_TOKEN) > -1;
}

function hasProcessor(input) {
  return input.indexOf(PIPE_TOKEN) > -1;
}

function createAlias(input) {
  var body = input.slice(ALIAS_TOKEN.length);
  return {
    type: 'alias',
    body: body
  };
}

function createString(input) {
  return {
    type: 'string',
    body: input
  };
}

function createTranslation(input) {
  return {
    type: 'translation',
    body: input
  };
}

function createVariable(input) {
  if (hasProcessor(input)) {
    var _input$split = input.split(PIPE_TOKEN),
        _input$split2 = _slicedToArray(_input$split, 2),
        body = _input$split2[0],
        processor = _input$split2[1];

    return {
      type: 'variable',
      body: body,
      processor: processor
    };
  }

  return {
    type: 'variable',
    body: input
  };
}

function parseBody(input) {
  if (input.length === 0) {
    return [];
  }

  if (!hasInterpolation(input)) {
    return [createString(input)];
  }

  var outerStart = input.indexOf(INTERPOLATION_START_TOKEN);
  var innerEnd = input.indexOf(INTERPOLATION_END_TOKEN);

  if (outerStart === -1 || innerEnd === -1) {
    assert(outerStart === innerEnd, E_TOKEN_MISSING);
  }

  var innerStart = outerStart + TOKEN_LENGTH;
  var outerEnd = innerEnd + TOKEN_LENGTH;

  if (outerStart > 0) {
    var chunk = input.slice(0, outerStart);
    var token = createString(chunk);
    var nextInput = input.slice(outerStart);
    return [token].concat(_toConsumableArray(parseBody(nextInput)));
  }

  if (outerStart === 0) {
    var _chunk = input.slice(innerStart, innerEnd).replace(/\s+/g, '');

    var _token = createVariable(_chunk);

    var _nextInput = input.slice(outerEnd);

    return [_token].concat(_toConsumableArray(parseBody(_nextInput)));
  }
}

function parse(input) {
  if (hasAlias(input)) {
    return createAlias(input);
  }

  if (!hasInterpolation(input)) {
    return createTranslation([createString(input)]);
  }

  return createTranslation(parseBody(input));
}

function isTranslationsRequest(action) {
  var isEffect = action.type === 'EFFECT_COMPOSE';
  var hasLanguageMeta = pathSatisfies(is(String), ['meta', '@rushplay/i18n', 'language'], action.payload);
  return isEffect && hasLanguageMeta;
}

function prepareAlias(language, parsedValue) {
  if (propEq('type', 'alias', parsedValue)) {
    return over(lensPath(['body']), function (body) {
      return "".concat(language, ":").concat(body);
    }, parsedValue);
  }

  return parsedValue;
}

function prepareTranslations(language, res) {
  var translations = res.value;
  var value = reduce(function (result, key) {
    var parsedValue = parse(translations[key]);
    return assoc("".concat(language, ":").concat(key), prepareAlias(language, parsedValue), result);
  }, {}, keys(translations));
  return assoc('value', value, res);
}

function forwardResponse(res) {
  return {
    type: 'EFFECT_FETCH_SUCCEEDED',
    payload: res
  };
}

function decomposeAction(action) {
  var lens = lensPath(['meta', 'steps', 0, 0]);
  return [set(lens, forwardResponse, action), path(['meta', '@rushplay/i18n', 'language'], action.payload), view(lens, action)];
}

var cache = createCache();
function middleware() {
  return function (store) {
    return function (next) {
      return function (originalAction) {
        if (isTranslationsRequest(originalAction)) {
          var _decomposeAction = decomposeAction(originalAction),
              _decomposeAction2 = _slicedToArray(_decomposeAction, 3),
              action = _decomposeAction2[0],
              language = _decomposeAction2[1],
              onSuccess = _decomposeAction2[2];

          var url = action.payload.payload.url;

          if (cache.check(url)) {
            var _request = cache.get(url);

            return _request.then(function (res) {
              return store.dispatch(onSuccess(res));
            });
          }

          var effect = next(action);
          var request = effect.then(function (action) {
            return prepareTranslations(language, action.payload);
          });
          cache.set(url, request, {
            ttl: 30 * 60 * 1000,
          });
          request.then(function (res) {
            return store.dispatch(onSuccess(res));
          });
          return effect;
        }

        return next(originalAction);
      };
    };
  };
}

var INITIAL_STATE = {};
var EFFECT_FETCH_TRANSLATIONS = '@rushplay/i18n/EFFECT_FETCH_TRANSLATIONS';
var TRANSLATIONS_UPDATED = '@rushplay/i18n/TRANSLATIONS_UPDATED';

function fetchTranslations(url, language) {
  return assocPath(['meta', '@rushplay/i18n', 'language'], toLower(language), fetch$1(url));
}

function updateTranslations(translations) {
  return {
    type: TRANSLATIONS_UPDATED,
    error: translations instanceof Error,
    payload: translations
  };
}
function fetch(url, language) {
  /*
   * TODO: We must track it as API request to make server wait for translations
   * to be downloaded.
   *
   * 1. Tracking must be extracted from API Client as it is independent piece
   * of logic.
   *
   * 2. Translations must be reworked not to fetch all the content. Possibly
   * look into `/translations` endpoint and `babel-plugin-tranform-translate`
   * branch in `@rushplay/i18n`
   */
  return [startTrackingRequest('translations'), bind(fetchTranslations(url, language), function (res) {
    return [updateTranslations(res.value), stopTrackingRequest('translations')];
  }, function () {
    return stopTrackingRequest('translations');
  })];
}
function reducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : INITIAL_STATE;
  var action = arguments.length > 1 ? arguments[1] : undefined;

  if (action.error) {
    return state;
  }

  switch (action.type) {
    case TRANSLATIONS_UPDATED:
      {
        return action.payload;
      }

    default:
      {
        return state;
      }
  }
}
function getKeys(state) {
  return keys(state);
}
/**
 * Check if translation exists
 * @param {Object} state
 * @param {Object} props
 * @param {string} props.translationKey
 * @returns {boolean}
 */

function hasTranslation(state, props) {
  return has(props.translationKey, state);
}
/**
 * Get translation string for given key
 * @param {Object} state
 * @param {Object} props
 * @param {string} props.translationKey
 * @returns {?string}
 */

function getTranslation(state, props) {
  var translation = prop(props.translationKey, state);

  if (translation && translation.type === 'alias') {
    return getTranslation(state, {
      translationKey: translation.body
    });
  }

  return translation;
}

var E_MISSING_PROCESSOR = new Error('Specified processor not found');
var E_UNKNOWN_ENTITY = new SyntaxError('Unknown entity');

function identity(x) {
  return x;
}

function isLiteral(body) {
  return body.startsWith('"') && body.endsWith('"');
}

function stringify(processors, data, input) {
  if (input.type !== 'translation') {
    throw E_UNKNOWN_ENTITY;
  }

  var result = '';

  for (var i = 0; i < input.body.length; i++) {
    var entity = input.body[i];

    switch (entity.type) {
      case 'string':
        {
          result += entity.body;
          break;
        }

      case 'variable':
        {
          var processor = entity.processor ? processors[entity.processor] : identity;

          if (typeof processor === 'undefined') {
            throw E_MISSING_PROCESSOR;
          }

          var value = isLiteral(entity.body) ? entity.body.slice(1, -1) : data[entity.body];

          if (typeof value === 'undefined') {
            result += "{{".concat(entity.body, "}}");
          } else {
            result += processor(value, data);
          }

          break;
        }

      default:
        {
          throw E_UNKNOWN_ENTITY;
        }
    }
  }

  return result;
}

var CaptureContext = React.createContext({});
var TranslationContext = React.createContext();

function useI18nCapture() {
  var context = React.useContext(CaptureContext);
  return React.useMemo(function () {
    return {
      onKeyUse: context.onKeyUse || function () {}
    };
  }, [context.onKeyUse]);
}
function TranslationCapture(props) {
  var value = React.useMemo(function () {
    return {
      onKeyUse: props.onKeyUse
    };
  }, [props.onKeyUse]);
  return React.createElement(CaptureContext.Provider, {
    value: value
  }, React.Children.only(props.children));
}
TranslationCapture.propTypes = {
  children: PropTypes.node,
  onKeyUse: PropTypes.func.isRequired
};

var TranslationFetcherBase =
/*#__PURE__*/
function (_React$PureComponent) {
  _inherits(TranslationFetcherBase, _React$PureComponent);

  function TranslationFetcherBase(props) {
    var _this;

    _classCallCheck(this, TranslationFetcherBase);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(TranslationFetcherBase).call(this, props));
    props.onFetch(props.url, props.language);
    return _this;
  }

  _createClass(TranslationFetcherBase, [{
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      var languageChanged = prevProps.language !== this.props.language;
      var urlChanged = prevProps.url !== this.props.url;

      if (languageChanged && urlChanged) {
        this.props.onFetch(this.props.url, this.props.language);
      }
    }
  }, {
    key: "render",
    value: function render() {
      return React.createElement(React.Fragment, {}, this.props.children);
    }
  }]);

  return TranslationFetcherBase;
}(React.PureComponent);
TranslationFetcherBase.propTypes = {
  children: PropTypes.element,
  language: PropTypes.string,
  url: PropTypes.string,
  onFetch: PropTypes.func.isRequired
};
var connector = connect(null, function (dispatch) {
  return {
    onFetch: function onFetch(url, language) {
      if (url && language) {
        dispatch(fetch(url, language));
      }
    }
  };
});
var TranslationFetcher = connector(TranslationFetcherBase);

function checkTranslations(_ref, entities) {
  var processors = _ref.processors;

  if (process.env.NODE_ENV !== 'production') {
    var _R$groupBy = groupBy(function (key) {
      var entity = entities[key];

      if (entity.type === 'alias') {
        return 'aliases';
      }

      if (entity.type === 'translation') {
        return 'translations';
      }

      return 'unknown';
    }, keys(entities)),
        aliases = _R$groupBy.aliases,
        translations = _R$groupBy.translations;

    forEach(function (key) {
      var alias = entities[key];
      var translation = entities[alias.body];

      if (translation == null) {
        // eslint-disable-next-line no-console
        console.error("\"".concat(key, "\" alias uses non-existent key \"").concat(alias.body, "\""));
      }
    }, defaultTo([], aliases));
    forEach(function (key) {
      var translation = entities[key];
      forEach(function (entity) {
        var isVariable = entity.type === 'variable';
        var hasProcessor = entity.processor != null;
        var processorExists = processors[entity.processor] != null;

        if (isVariable && hasProcessor && !processorExists) {
          // eslint-disable-next-line no-console
          console.error("\"".concat(key, "\" is using non-existent processor \"").concat(entity.processor, "\""));
        }
      }, translation.body);
    }, defaultTo([], translations));
  }
}

function useI18nContextValue(props) {
  var i18nCapture = useI18nCapture();
  var translations = useSelector(props.stateResolver, equals);
  var keys = React.useMemo(function () {
    return getKeys(translations);
  }, [translations]);
  var globals = useSelector(props.variableResolver, equals);
  React.useEffect(function () {
    checkTranslations({
      processors: props.processors
    }, translations);
  }, [translations, props.processors]);
  var translate = React.useCallback(function (key, variables) {
    var translationKey = props.keyResolver(keys, key);
    i18nCapture.onKeyUse(translationKey);
    var translation = getTranslation(translations, {
      translationKey: translationKey
    });

    if (!translation) {
      return "[".concat(translationKey, "]");
    }

    try {
      return stringify(props.processors, merge(globals, variables), translation);
    } catch (error) {
      props.onError(error);
      return "[".concat(translationKey, "]");
    }
  }, [globals, i18nCapture.onKeyUse, keys, translations, props.keyResolver, props.processors, props.onError]);
  var hasTranslation$1 = React.useCallback(function (key) {
    var translationKey = props.keyResolver(keys, key);
    i18nCapture.onKeyUse(translationKey);
    return hasTranslation(translations, {
      translationKey: translationKey
    });
  }, [i18nCapture.onKeyUse, translations]);
  return React.useMemo(function () {
    return {
      hasTranslation: hasTranslation$1,
      translate: translate
    };
  }, [hasTranslation$1, translate]);
}

function useI18n() {
  var context = React.useContext(TranslationContext);

  if (context == null) {
    return {
      hasTranslation: function hasTranslation() {
        return true;
      },
      translate: function translate(key) {
        return "[dev:".concat(key, "]");
      }
    };
  }

  return context;
}
function TranslationProvider(props) {
  var value = useI18nContextValue(props);
  return React.createElement(TranslationFetcher, {
    language: props.language,
    url: props.url
  }, React.createElement(TranslationContext.Provider, {
    value: value
  }, React.Children.only(props.children)));
}
TranslationProvider.defaultProps = {
  keyResolver: function keyResolver(key) {
    return key;
  },
  processors: {},
  variableResolver: function variableResolver() {
    return {};
  },
  onError: function onError() {}
};
TranslationProvider.propTypes = {
  children: PropTypes.element,
  keyResolver: PropTypes.func,
  language: PropTypes.string,
  processors: PropTypes.object,
  stateResolver: PropTypes.func.isRequired,
  variableResolver: PropTypes.func,
  url: PropTypes.string,
  onError: PropTypes.func
};

function withTranslate(WrappedComponent) {
  return function TranslationWrapper(props) {
    var context = useI18n();
    var deprecatedProps = {
      isExistingTranslation: function isExistingTranslation() {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('DEPRECATED', '"isExistingTranslation" is deprecated and will be removed.', 'Use "hasTranslation".');
        }

        context.hasTranslation.apply(context, arguments);
      }
    };
    return React.createElement(WrappedComponent, Object.assign({}, props, context, deprecatedProps));
  };
}

export { EFFECT_FETCH_TRANSLATIONS, TRANSLATIONS_UPDATED, TranslationCapture, TranslationProvider, fetch, getKeys, getTranslation, hasTranslation, middleware, parse, reducer, stringify, updateTranslations, useI18n, useI18nCapture, withTranslate };
